/*
  One card: its image, its finish breakdown, and every grading of it (§6.5, §7.4).

  This view fetches its own gradings AND feeds a second consumer of the filtered rows —
  FinishTable pivots the same rows the table pages — so it takes task 7's shape: the view
  owns useTableState and selectTableRows, and CardViewTable renders what it is handed.

  Two things that were wrong here and are fixed rather than carried:

    - **the filter is inside the table state now.** It was a useState of its own that the
      table's reducer never saw (§6.5, tableState.ts's opening comment names this file),
      so typing in the box left the pagination on whatever page it was already on and a
      filter that shortened the results below it painted a blank table.
    - **the RTK Query error is passed through** rather than discarded. The table has an
      error state and nothing could reach it (§12.20).

  The h1 is the card name (§3.5, §12.31). This page has no logo standing in for a title,
  so unlike GameView's it is visible.
*/

import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import Breadcrumbs from "components/Breadcrumbs";
import CardHeader, { CARD_GRADINGS_LIMIT } from "components/CardHeader";
import FilterSearch from "components/FilterSearch";
import CardViewTable, { cardViewColumns } from "components/Tables/CardViewTable";
import { FinishTable } from "components/FinishTable";

import { selectTableRows, useTableState } from "components/DataTable";

import { useGetGamesQuery, useSearchGradingsQuery } from "services/pcgPublicV2";
import { sanitizeURIPathSegment } from "helpers/routing";
import { formatCount } from "lib";
import { cleanSetName } from "lib/setName";

/*
  Every grading of one card, paged on the client — up to 200, the API's cap. CardHeader
  makes the same request (one constant, so RTK shares it) and takes its counts from the
  set feed instead, which is complete; the heading over the table says when the list is not.
*/
const FETCH_LIMIT = CARD_GRADINGS_LIMIT;

/*
  Heading, hairline, count: the "All Sets" / "All Cards" header, for the gradings. When the
  card has more gradings than the API returns at once it says so, rather than letting the
  table's own count pass for the card's.
*/
export const GradingsHeading = ({
  title = "All Gradings",
  total,
  listed,
  isFetching,
}: {
  title?: string;
  total: number;
  listed: number;
  isFetching: boolean;
}) => (
  <div className="flex items-center gap-3">
    <h2 className="font-display text-lg font-semibold text-pcg-text sm:text-xl">{title}</h2>
    <span aria-hidden="true" className="h-px flex-1 bg-pcg-goldMuted" />
    {!isFetching ? (
      <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
        {listed < total
          ? `${formatCount(listed)} of ${formatCount(total)} gradings listed`
          : `${formatCount(total)} ${total === 1 ? "grading" : "gradings"}`}
      </span>
    ) : null}
  </div>
);

function CardView() {
  const params = useParams();
  const { gameCode = "", setName = "", number = "" } = params;

  const { data: games = [] } = useGetGamesQuery();
  const gameInfo = games.find(
    g => g.gameCode.toLowerCase() === gameCode.toLowerCase()
  );

  /* error is no longer discarded: DataTable takes it and renders the error state. */
  const { data: gradingsData, isFetching, error } = useSearchGradingsQuery({
    gameCode,
    setName,
    cardNumber: number,
    offset: 0,
    limit: FETCH_LIMIT,
  });

  const data = useMemo(() => gradingsData?.data ?? [], [gradingsData]);
  const gradingsTotal = gradingsData?.pagination?.total ?? data.length;

  const cardInfo = data.length > 0 ? data[0].cardInfo : null;
  const cardName = cardInfo?.cardName || "";

  /* 25 was the old reducer's page size (limit: 25); DEFAULT_ROWS_PER_PAGE is 10. */
  const table = useTableState({ orderBy: "finalGrade", order: "desc", rowsPerPage: 25 });
  const { setFilter } = table;
  const view = useMemo(
    () => selectTableRows(data, cardViewColumns, table.state),
    [data, table.state]
  );

  /*
    A different card is a different list of gradings, so the filter clears. FilterSearch
    holds the text a visitor is mid-way through typing, so it is keyed on the card below
    to clear the visible box along with the state — the old setSearch("") never did.
  */
  useEffect(() => {
    setFilter("");
  }, [gameCode, setName, number, setFilter]);

  return (
    <>
      <Helmet title={setName || ""} />
      <div className="skin-stack flex flex-col gap-6">
        <Breadcrumbs
          crumbs={[
            {
              url: "/",
              text: "Home",
            },
            {
              url: `/${gameCode}`,
              text: gameInfo?.name || "",
            },
            {
              url: `/${gameCode}/${sanitizeURIPathSegment(setName)}`,
              /* Display name only; the url stays raw (pop-language-split D10). */
              text: setName ? cleanSetName(setName) : "",
            },
            {
              url: "",
              text: `${cardName || "N/A"} (#${number})`,
            },
          ]}
        />

        {/* The card's h1, art and figures. */}
        <CardHeader gameCode={gameCode} setName={setName} number={number} />

        <GradingsHeading total={gradingsTotal} listed={data.length} isFetching={isFetching} />

        {/* The pivot reads the filtered rows, not the one page the table shows. */}
        <FinishTable data={view.filteredRows} />

        <div className="flex justify-end">
          <FilterSearch
            key={`${gameCode}/${setName}/${number}`}
            onChange={setFilter}
            label="Search Card Gradings"
            className="w-full sm:w-72"
          />
        </div>

        <CardViewTable
          columns={cardViewColumns}
          view={view}
          table={table}
          isLoading={isFetching}
          error={error}
        />
      </div>
    </>
  );
}

export default CardView;
